<template>
  <div>
    <div class="mb-4">
      <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div>
          <h2 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Begroting
          </h2>
          <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Stel maandelijkse begrotingsdoelen in per type (uren omzet en derden omzet). Deze worden
            als stippellijnen getoond in de Omzet-grafiek.
          </p>
        </div>
        <button
          @click="openBudgetForm(null)"
          class="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
        >
          <PlusIcon class="h-4 w-4" />
          <span>Jaar toevoegen</span>
        </button>
      </div>
    </div>

    <BudgetOverview
      :budgets="budgets || []"
      :is-loading="isLoadingBudgets"
      :error="budgetsError"
      @edit="openBudgetForm"
      @delete-year="confirmDeleteBudgetYear"
    />

    <!-- Budget Year Form Modal -->
    <BudgetYearForm
      :open="showBudgetForm"
      :year="budgetEditYear"
      :budgets="budgets || []"
      @close="showBudgetForm = false"
      @save="handleSaveBudget"
    />

    <!-- Budget Delete Year Modal -->
    <div
      v-if="showBudgetDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="showBudgetDeleteModal = false"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Begroting {{ budgetDeleteYear }} verwijderen?
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Alle begrotingswaarden voor {{ budgetDeleteYear }} worden permanent verwijderd.
        </p>
        <div class="flex justify-end gap-3">
          <button
            @click="showBudgetDeleteModal = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Annuleren
          </button>
          <button
            @click="handleDeleteBudgetYear"
            :disabled="isDeletingBudgetYear"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {{ isDeletingBudgetYear ? 'Verwijderen...' : 'Verwijderen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { budgetService } from '@/services/adminService.js'

import BudgetOverview from '@/components/admin/BudgetManagement/BudgetOverview.vue'
import BudgetYearForm from '@/components/admin/BudgetManagement/BudgetYearForm.vue'

const showBudgetForm = ref(false)
const budgetEditYear = ref(null)
const showBudgetDeleteModal = ref(false)
const budgetDeleteYear = ref(null)
const isDeletingBudgetYear = ref(false)

const {
  data: budgets,
  isLoading: isLoadingBudgets,
  error: budgetsError,
  refetch: refetchBudgets
} = useQuery({
  queryKey: ['budget-targets'],
  queryFn: () => budgetService.getAll(),
  staleTime: 1000 * 60 * 5
})

const openBudgetForm = year => {
  budgetEditYear.value = year
  showBudgetForm.value = true
}

const handleSaveBudget = async ({ year, months }) => {
  try {
    for (const m of months) {
      await budgetService.upsert({
        year,
        month: m.month,
        budget_hours_revenue: m.budget_hours_revenue,
        budget_derden_omzet: m.budget_derden_omzet,
        actual_derden_omzet: m.actual_derden_omzet
      })
    }
    await refetchBudgets()
    showBudgetForm.value = false
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Fout bij opslaan van begroting')
  }
}

const confirmDeleteBudgetYear = year => {
  budgetDeleteYear.value = year
  showBudgetDeleteModal.value = true
}

const handleDeleteBudgetYear = async () => {
  if (!budgetDeleteYear.value) return
  isDeletingBudgetYear.value = true
  try {
    const yearBudgets = (budgets.value || []).filter(b => b.year === budgetDeleteYear.value)
    for (const b of yearBudgets) {
      await budgetService.delete(b.id)
    }
    await refetchBudgets()
    showBudgetDeleteModal.value = false
    budgetDeleteYear.value = null
  } catch (err) {
    console.error('Error deleting budget year:', err)
    alert('Fout bij verwijderen van begroting')
  } finally {
    isDeletingBudgetYear.value = false
  }
}
</script>
